package com.arena_web.core.viewmodel

import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.ClubAdminRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class ClubSettingsUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val club: ClubDto? = null,
    val amenities: List<String> = emptyList(),
    val posts: List<ClubPostDto> = emptyList(),

    // Rule editing
    val newRuleText: String = "",
    val isSavingRule: Boolean = false,

    // Amenities
    val isSavingAmenities: Boolean = false,

    // Post creation
    val newPostText: String = "",
    val postImageUrls: List<String> = emptyList(),
    val isUploadingPostImage: Boolean = false,
    val isCreatingPost: Boolean = false,
    val showComposePost: Boolean = false,
)

class ClubSettingsViewModel(
    private val clubAdminRepository: ClubAdminRepository
) : BaseViewModel() {

    private val _uiState = MutableStateFlow(ClubSettingsUiState())
    val uiState: StateFlow<ClubSettingsUiState> = _uiState.asStateFlow()

    private var currentClubId: String? = null

    override fun onCreate() {
        super.onCreate()
    }

    fun loadClub(clubId: String) {
        if (clubId == currentClubId && _uiState.value.club != null) return
        currentClubId = clubId
        _uiState.update { it.copy(isLoading = true, error = null) }

        viewModelScope.launch {
            clubAdminRepository.getClub(clubId).onSuccess { club ->
                _uiState.update { it.copy(club = club, isLoading = false) }
            }.onFailure { e ->
                _uiState.update { it.copy(isLoading = false, error = e.message) }
            }
        }
        loadAmenities(clubId)
        loadFeed(clubId)
    }

    fun switchClub(clubId: String) {
        currentClubId = null
        _uiState.update {
            ClubSettingsUiState()
        }
        loadClub(clubId)
    }

    private fun loadAmenities(clubId: String) {
        viewModelScope.launch {
            clubAdminRepository.getAmenities(clubId).onSuccess { amenities ->
                _uiState.update { it.copy(amenities = amenities) }
            }
        }
    }

    private fun loadFeed(clubId: String) {
        viewModelScope.launch {
            clubAdminRepository.getFeed(clubId).onSuccess { posts ->
                _uiState.update { it.copy(posts = posts) }
            }
        }
    }

    // -- Rules --

    fun updateNewRuleText(text: String) {
        _uiState.update { it.copy(newRuleText = text) }
    }

    fun addRule() {
        val clubId = currentClubId ?: return
        val ruleText = _uiState.value.newRuleText.trim()
        if (ruleText.isEmpty()) return

        val existingRules = _uiState.value.club?.rules?.map { RuleTextEntry(it.text) } ?: emptyList()
        val updatedRules = existingRules + RuleTextEntry(ruleText)

        _uiState.update { it.copy(isSavingRule = true) }
        viewModelScope.launch {
            clubAdminRepository.updateRules(clubId, UpdateClubRulesRequest(updatedRules))
                .onSuccess { rules ->
                    _uiState.update { state ->
                        state.copy(
                            club = state.club?.copy(rules = rules),
                            newRuleText = "",
                            isSavingRule = false
                        )
                    }
                }.onFailure {
                    _uiState.update { it.copy(isSavingRule = false) }
                }
        }
    }

    // -- Amenities --

    fun updateAmenities(amenityTypes: List<String>) {
        val clubId = currentClubId ?: return
        _uiState.update { it.copy(isSavingAmenities = true) }
        viewModelScope.launch {
            clubAdminRepository.updateAmenities(clubId, amenityTypes)
                .onSuccess { amenities ->
                    _uiState.update { it.copy(amenities = amenities, isSavingAmenities = false) }
                }.onFailure {
                    _uiState.update { it.copy(isSavingAmenities = false) }
                }
        }
    }

    // -- Posts --

    fun showComposePost() {
        _uiState.update { it.copy(showComposePost = true) }
    }

    fun hideComposePost() {
        _uiState.update { it.copy(showComposePost = false, newPostText = "", postImageUrls = emptyList()) }
    }

    fun updateNewPostText(text: String) {
        _uiState.update { it.copy(newPostText = text) }
    }

    fun uploadPostImage(fileBytes: ByteArray, fileName: String, contentType: String) {
        if (_uiState.value.postImageUrls.size >= 2) return
        _uiState.update { it.copy(isUploadingPostImage = true) }
        viewModelScope.launch {
            clubAdminRepository.uploadClubImage(fileBytes, fileName, contentType)
                .onSuccess { response ->
                    _uiState.update { state ->
                        state.copy(
                            postImageUrls = state.postImageUrls + response.imageUrl,
                            isUploadingPostImage = false
                        )
                    }
                }.onFailure {
                    _uiState.update { it.copy(isUploadingPostImage = false) }
                }
        }
    }

    fun removePostImage(index: Int) {
        _uiState.update { state ->
            state.copy(postImageUrls = state.postImageUrls.filterIndexed { i, _ -> i != index })
        }
    }

    fun createPost() {
        val clubId = currentClubId ?: return
        val content = _uiState.value.newPostText.trim()
        if (content.isEmpty()) return

        _uiState.update { it.copy(isCreatingPost = true) }
        viewModelScope.launch {
            clubAdminRepository.createPost(
                clubId,
                CreateClubPostRequest(content = content, imageUrls = _uiState.value.postImageUrls)
            ).onSuccess {
                    _uiState.update {
                        it.copy(
                            newPostText = "",
                            postImageUrls = emptyList(),
                            isCreatingPost = false,
                            showComposePost = false
                        )
                    }
                    loadFeed(clubId)
                }.onFailure {
                    _uiState.update { it.copy(isCreatingPost = false) }
                }
        }
    }

    fun deletePost(postId: String) {
        val clubId = currentClubId ?: return
        viewModelScope.launch {
            clubAdminRepository.deletePost(clubId, postId).onSuccess {
                loadFeed(clubId)
            }
        }
    }

    // -- Club Info --

    fun updateClubInfo(
        name: String,
        address: String,
        latitude: Double? = null,
        longitude: Double? = null,
        instagramUrl: String? = null,
        websiteUrl: String? = null,
        googleSearchUrl: String? = null,
        googleMapsUrl: String? = null,
    ) {
        val clubId = currentClubId ?: return
        viewModelScope.launch {
            clubAdminRepository.updateClub(
                clubId,
                UpdateClubRequest(
                    name = name,
                    address = address,
                    latitude = latitude,
                    longitude = longitude,
                    // Blank clears the link on the backend; null keeps it.
                    instagramUrl = instagramUrl,
                    websiteUrl = websiteUrl,
                    googleSearchUrl = googleSearchUrl,
                    googleMapsUrl = googleMapsUrl,
                )
            ).onSuccess { club ->
                _uiState.update { it.copy(club = club) }
            }
        }
    }

    fun deleteClub(onSuccess: () -> Unit) {
        val clubId = currentClubId ?: return
        viewModelScope.launch {
            clubAdminRepository.deleteClub(clubId)
                .onSuccess {
                    currentClubId = null
                    _uiState.update { ClubSettingsUiState() }
                    onSuccess()
                }
        }
    }

    // -- Gallery --

    fun uploadBanner(fileBytes: ByteArray, fileName: String, contentType: String) {
        val clubId = currentClubId ?: return
        viewModelScope.launch {
            clubAdminRepository.uploadClubImage(fileBytes, fileName, contentType)
                .onSuccess { response ->
                    clubAdminRepository.updateClub(clubId, UpdateClubRequest(mainPhotoUrl = response.imageUrl))
                        .onSuccess { club ->
                            _uiState.update { it.copy(club = club) }
                        }
                }
        }
    }

    fun uploadGalleryImage(fileBytes: ByteArray, fileName: String, contentType: String) {
        val clubId = currentClubId ?: return
        viewModelScope.launch {
            clubAdminRepository.uploadClubImage(fileBytes, fileName, contentType)
                .onSuccess { response ->
                    val existing = _uiState.value.club?.gallery?.map { GalleryImageEntry(it.imageUrl) } ?: emptyList()
                    val updated = existing + GalleryImageEntry(response.imageUrl)
                    clubAdminRepository.updateGallery(clubId, UpdateClubGalleryRequest(updated))
                        .onSuccess { gallery ->
                            _uiState.update { state ->
                                state.copy(club = state.club?.copy(gallery = gallery))
                            }
                        }
                }
        }
    }

    fun replaceGalleryImage(index: Int, fileBytes: ByteArray, fileName: String, contentType: String) {
        val clubId = currentClubId ?: return
        val existing = _uiState.value.club?.gallery ?: return
        if (index !in existing.indices) return

        viewModelScope.launch {
            clubAdminRepository.uploadClubImage(fileBytes, fileName, contentType)
                .onSuccess { response ->
                    val updated = existing.mapIndexed { i, img ->
                        if (i == index) GalleryImageEntry(response.imageUrl) else GalleryImageEntry(img.imageUrl)
                    }
                    clubAdminRepository.updateGallery(clubId, UpdateClubGalleryRequest(updated))
                        .onSuccess { gallery ->
                            _uiState.update { state ->
                                state.copy(club = state.club?.copy(gallery = gallery))
                            }
                        }
                }
        }
    }

    fun deleteGalleryImage(index: Int) {
        val clubId = currentClubId ?: return
        val existing = _uiState.value.club?.gallery ?: return
        if (index !in existing.indices) return

        val updated = existing.filterIndexed { i, _ -> i != index }.map { GalleryImageEntry(it.imageUrl) }
        viewModelScope.launch {
            clubAdminRepository.updateGallery(clubId, UpdateClubGalleryRequest(updated))
                .onSuccess { gallery ->
                    _uiState.update { state ->
                        state.copy(club = state.club?.copy(gallery = gallery))
                    }
                }
        }
    }
}
